import type BlueBirdPromise from 'bluebird';
import type { FunctionComponent } from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import _ from 'underscore';

import { useMemberId } from '@trello/authentication';
import { Spinner } from '@trello/nachos/spinner';
import type { PIIString } from '@trello/privacy';
import {
  dangerouslyConvertPrivacyString,
  EMPTY_PII_STRING,
} from '@trello/privacy';

import { ErrorMessage } from './ErrorMessage';
import {
  isValidEmail,
  simpleDomainRestriction,
  sortBoardMembershipsByType,
} from './HelperFunctions';
import type { Membership } from './InviteMembership.types';
import { LoggedInUserText } from './LoggedInUserText';
import { useMemberLoginQuery } from './MemberLoginQuery.generated';
import { NotLoggedInUserText } from './NotLoggedInUserText';

export const getMemberNames = (memberships: Membership[], numNames: number) => {
  return memberships
    .slice(0, numNames)
    .map((membership: Membership) => membership.member.fullName)
    .join(', ');
};

export const getMembersString = (sortedMemberships: Membership[]) => {
  const membershipsLength = sortedMemberships.length;

  if (membershipsLength === 0) {
    return null;
  } else if (membershipsLength === 1) {
    return (
      <FormattedMessage
        id="templates.invitation_link.join-in-collaborating-on-this-board-single"
        defaultMessage="Join {memberName} in collaborating on this board."
        description="Join member in collaborating on this board single"
        values={{
          memberName: sortedMemberships[0].member.fullName,
        }}
      />
    );
  } else if (membershipsLength === 2) {
    return (
      <FormattedMessage
        id="templates.invitation_link.join-in-collaborating-on-this-board-few"
        defaultMessage="Join {memberNames} and {lastMember} in collaborating on this board."
        description="Join members in collaborating on this board"
        values={{
          memberNames: sortedMemberships[0].member.fullName,
          lastMember: sortedMemberships[1].member.fullName,
        }}
      />
    );
  } else if (membershipsLength === 3) {
    return (
      <FormattedMessage
        id="templates.invitation_link.join-in-collaborating-on-this-board-few"
        defaultMessage="Join {memberNames} and {lastMember} in collaborating on this board."
        description="Join members in collaborating on this board"
        values={{
          memberNames: getMemberNames(sortedMemberships, 2),
          lastMember: sortedMemberships[2].member.fullName,
        }}
      />
    );
  }

  return (
    <FormattedMessage
      id="templates.invitation_link.join-in-collaborating-on-this-board-many"
      defaultMessage="Join {memberNames} and {numberOthers} others in collaborating on this board."
      description="Join members in collaborating on this board many"
      values={{
        memberNames: getMemberNames(sortedMemberships, 3),
        numberOthers: membershipsLength - 3,
      }}
    />
  );
};

export interface InvitationLinkPageProps {
  inviterId: string;
  inviterName?: PIIString;
  modelNameText: string;
  emailRestrictions: string[];
  allowManagedMembers: boolean;
  inviteIdEnterprise: string;
  willJoinOnConfirm: boolean;
  transferUrl: string;
  modelName: 'Board' | 'Team';
  fxJoin: (traceId: string) => BlueBirdPromise<void>;
  fxQueueJoin: () => void;
  analyticsContainers: {
    [key: string]: {
      id: string;
    };
  };
  memberships?: Membership[];
  inviteRestrictionUrlFromEnterprise?: string;
}

const emptyMemberships: Membership[] = [];
export const InvitationLinkPage: FunctionComponent<InvitationLinkPageProps> = ({
  inviterId,
  inviterName,
  modelNameText,
  emailRestrictions,
  allowManagedMembers,
  inviteIdEnterprise,
  willJoinOnConfirm,
  transferUrl = '',
  modelName,
  fxJoin,
  fxQueueJoin,
  analyticsContainers,
  memberships = emptyMemberships,
  inviteRestrictionUrlFromEnterprise,
}) => {
  const memberId = useMemberId();
  const { data, loading } = useMemberLoginQuery({
    variables: {
      memberId,
    },
    waitOn: ['MemberHeader'],
  });

  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEnterpriseError, setIsEnterpriseError] = useState(false);
  const [
    isWorkspaceInviteRestrictionError,
    setIsWorkspaceInviteRestrictionError,
  ] = useState(false);

  const me = data?.member;
  const isConfirmed = Boolean(me?.confirmed);
  const hasValidEmail = useMemo(() => {
    return Boolean(
      me &&
        _.chain(me.logins)
          .pluck('email')
          .any((email) =>
            isValidEmail(
              emailRestrictions,
              dangerouslyConvertPrivacyString(email),
            ),
          )
          .value(),
    );
  }, [emailRestrictions, me]);

  const requiredDomain =
    emailRestrictions.length === 1
      ? simpleDomainRestriction(emailRestrictions[0])
      : null;

  const hasValidManagedStatus = Boolean(
    allowManagedMembers &&
      inviteIdEnterprise &&
      inviteIdEnterprise === me?.idEnterprise,
  );

  const returnUrl = window.location.pathname;

  const sortedMemberships = useMemo(
    () => sortBoardMembershipsByType(memberships, inviterId),
    [inviterId, memberships],
  );

  if (loading) {
    return <Spinner centered />;
  }

  return (
    <div className="invitation-link">
      <div className="invitation-link-description">
        {modelName === 'Board' ? (
          <FormattedMessage
            id="templates.invitation_link.board-shared-with-you-safe"
            defaultMessage="{inviterName} shared {name} with you."
            description="Board shared with you"
            values={{
              inviterName: <strong key="inviter-name">{inviterName}</strong>,
              name: <strong key="model-name">{modelNameText}</strong>,
            }}
          />
        ) : (
          <FormattedMessage
            id="templates.invitation_link.youve-been-invited-safe"
            defaultMessage="{inviterName} invited you to {name}!"
            description="You have been invited"
            values={{
              inviterName: <strong key="inviter-name">{inviterName}</strong>,
              name: <strong key="model-name">{modelNameText}</strong>,
            }}
          />
        )}
      </div>

      {pendingEmail ? (
        <p className="invitation-link-explanation">
          <FormattedMessage
            id="templates.invitation_link.waiting-for-you-to-click-on-the-link-we-sent-to-email-safe"
            defaultMessage="We're waiting for you to click the link in the email we sent to {email}…"
            description="Waiting for you to click on the link we sent to email"
            values={{
              email: <strong key="pending-email">{pendingEmail}</strong>,
            }}
          />
        </p>
      ) : me ? (
        <LoggedInUserText
          isConfirmed={isConfirmed}
          hasValidEmail={hasValidEmail}
          hasValidManagedStatus={hasValidManagedStatus}
          fullName={me.fullName || EMPTY_PII_STRING}
          returnUrl={returnUrl}
          modelName={modelName}
          fxJoin={fxJoin}
          setErrorMessage={setErrorMessage}
          setIsEnterpriseError={setIsEnterpriseError}
          setIsWorkspaceInviteRestrictionError={
            setIsWorkspaceInviteRestrictionError
          }
          analyticsContainers={analyticsContainers}
          requiredDomain={requiredDomain}
          emailRestrictions={emailRestrictions}
          setPendingEmail={setPendingEmail}
          willJoinOnConfirm={willJoinOnConfirm}
          fxQueueJoin={fxQueueJoin}
        />
      ) : (
        <NotLoggedInUserText
          requiredDomain={requiredDomain}
          emailRestrictions={emailRestrictions}
          returnUrl={returnUrl}
          modelName={modelName}
          analyticsContainers={analyticsContainers}
          fxQueueJoin={fxQueueJoin}
          memberString={getMembersString(sortedMemberships)}
          memberships={sortedMemberships}
        />
      )}

      {errorMessage && (
        <ErrorMessage
          transferUrl={transferUrl}
          errorMessage={errorMessage}
          isEnterpriseError={isEnterpriseError}
          inviteRestrictionUrlFromEnterprise={
            isWorkspaceInviteRestrictionError
              ? inviteRestrictionUrlFromEnterprise
              : undefined
          }
        />
      )}
    </div>
  );
};
